import type { DragEndEvent, DragOverEvent, DragStartEvent } from "@dnd-kit/core"
import { DragOverlay } from "@dnd-kit/core"
import {
  type Tag,
  addDaysToDate,
  generateDateRange,
  isAfterDate,
  isBeforeDate,
  isSameDate,
} from "@taskfortress/core"
import { SyncItemType, type Task, TaskStatus } from "@taskfortress/core"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FlatList } from "react-native"
import { useData } from "../../providers/DataProvider"
import { DragDropProvider } from "../dragDrop"
import { Box } from "../gluestack/box"
import { DroppableDayColumn } from "./DroppableDayColumn"
import { TaskItem } from "./TaskItem"
import {
  DATE_RANGE_BUFFER,
  DAY_COLUMN_WIDTH,
  DRAG_SAVE_DEBOUNCE,
  DRAG_SCROLL_TIMEOUT,
  LOADING_DELAY,
  SCROLL_STOP_DELAY,
  SCROLL_THROTTLE,
  VISIBLE_DAYS_RANGE,
} from "./constants"
import {
  getTasksForDate,
  groupTasksByDate,
  groupTasksByStatus,
} from "./taskGrouping"

interface TaskListDesktopProps {
  currentDate: string
  onCurrentDateChange: (date: string) => void
  selectedTag?: Tag | null
}

interface DragState {
  draggedTaskId: string | null
  draggedTask: Task | null
  sourceDate: string | null
  originalIndex: number | null
  hoverDate: string | null
  hoverIndex: number | null
  wasSuccessfulDrop: boolean
}

// Generate a new date range centered around a specific date
const generateNewDateRange = (centerDate: string) => {
  const start = addDaysToDate(centerDate, -VISIBLE_DAYS_RANGE)
  const end = addDaysToDate(centerDate, VISIBLE_DAYS_RANGE)
  return generateDateRange(start, end)
}

export const TaskListDesktop: React.FC<TaskListDesktopProps> = ({
  currentDate,
  onCurrentDateChange,
  selectedTag,
}) => {
  const { tasks, tasksForSelectedTag, saveItems } = useData()
  const flatListRef = useRef<FlatList>(null)
  const manualScrollingFlagTimerRef = useRef<NodeJS.Timeout | null>(null)
  const scrollStopTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  // const [nextVisibleDateAfterUserScroll, setNextVisibleDateAfterUserScroll] = useState<string>()
  const nextVisibleDateAfterUserScroll = useRef<string>()
  const [renderDateRange, setRenderDateRange] = useState<string[]>(
    generateNewDateRange(currentDate),
  )
  const [dragState, setDragState] = useState<DragState>({
    draggedTaskId: null,
    draggedTask: null,
    sourceDate: null,
    originalIndex: null,
    hoverDate: null,
    hoverIndex: null,
    wasSuccessfulDrop: false,
  })

  // Group tasks by due date
  const tasksByDate = useMemo(() => {
    return groupTasksByDate(tasksForSelectedTag)
  }, [tasksForSelectedTag])

  // Group all tasks by due date (unfiltered)
  const allTasksByDate = useMemo(() => {
    return groupTasksByDate(tasks)
  }, [tasks])

  // Handle drag start
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const draggedTaskId = event.active.id as string
      const draggedTask = event.active.data.current?.task as Task

      if (!draggedTask || draggedTask.status !== TaskStatus.TODO) {
        return
      }

      const sourceDate = draggedTask.dueDate
      if (!sourceDate) return

      // Find the original index in the TODO list for the source date
      const sourceTasks = getTasksForDate(tasksByDate, sourceDate)
      const sourceTasksByStatus = groupTasksByStatus(sourceTasks)
      const originalIndex = sourceTasksByStatus.todo.findIndex(
        (task) => task.id === draggedTaskId,
      )

      setDragState({
        draggedTaskId,
        draggedTask,
        sourceDate,
        originalIndex,
        hoverDate: null,
        hoverIndex: null,
        wasSuccessfulDrop: false,
      })
    },
    [tasksByDate],
  )

  // Handle drag over
  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { over } = event

      if (!over || !dragState.draggedTaskId) {
        return
      }

      const overId = over.id as string

      // Parse the drop zone ID to get the hover date and position
      const dropZoneMatch = overId.match(/^(.+)-todo-(\d+)$/)
      if (dropZoneMatch) {
        const hoverDate = dropZoneMatch[1]
        const hoverIndex = parseInt(dropZoneMatch[2], 10)

        // Only update if the hover position has changed
        if (
          hoverDate !== dragState.hoverDate ||
          hoverIndex !== dragState.hoverIndex
        ) {
          setDragState((prev) => ({
            ...prev,
            hoverDate,
            hoverIndex,
          }))
        }
      }
    },
    [dragState.draggedTaskId, dragState.hoverDate, dragState.hoverIndex],
  )

  // Handle drag end
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || !active.data.current?.task) {
        // Reset drag state
        setDragState({
          draggedTaskId: null,
          draggedTask: null,
          sourceDate: null,
          originalIndex: null,
          hoverDate: null,
          hoverIndex: null,
          wasSuccessfulDrop: false,
        })
        return
      }

      const draggedTask = active.data.current.task as Task
      const overId = over.id as string

      // Only handle TODO task drops
      if (draggedTask.status !== TaskStatus.TODO) {
        setDragState({
          draggedTaskId: null,
          draggedTask: null,
          sourceDate: null,
          originalIndex: null,
          hoverDate: null,
          hoverIndex: null,
          wasSuccessfulDrop: false,
        })
        return
      }

      // Check if this is a valid drop zone immediately
      const dropZoneMatch = overId.match(/^(.+)-todo-(\d+)$/)
      const isValidDrop = !!dropZoneMatch

      // Set success flag immediately before any async work
      setDragState((prev) => ({
        ...prev,
        wasSuccessfulDrop: isValidDrop,
      }))

      if (!isValidDrop) {
        // Reset drag state for invalid drops
        setDragState({
          draggedTaskId: null,
          draggedTask: null,
          sourceDate: null,
          originalIndex: null,
          hoverDate: null,
          hoverIndex: null,
          wasSuccessfulDrop: false,
        })
        return
      }

      try {
        const targetDate = dropZoneMatch[1]
        const newIndex = parseInt(dropZoneMatch[2], 10)

        // Get target date tasks and filter out the dragged task if it's the same date
        const targetTasks = getTasksForDate(tasksByDate, targetDate)
        const targetTasksByStatus = groupTasksByStatus(targetTasks)
        const targetTodoTasks = targetTasksByStatus.todo.filter(
          (t) => t.id !== draggedTask.id,
        )

        // Insert the dragged task at the new position
        targetTodoTasks.splice(newIndex, 0, {
          ...draggedTask,
          dueDate: targetDate,
          updatedAt: new Date().toISOString(),
        })

        // Update indices for all affected tasks in the target date
        const updatedTargetTasks = targetTodoTasks.map((task, index) => ({
          ...task,
          index,
          updatedAt: new Date().toISOString(),
        }))

        let allUpdatedTasks = updatedTargetTasks

        // If moving between different dates, also update source date task indices
        if (targetDate !== dragState.sourceDate && dragState.sourceDate) {
          const sourceTasks = getTasksForDate(tasksByDate, dragState.sourceDate)
          const sourceTasksByStatus = groupTasksByStatus(sourceTasks)
          const remainingSourceTasks = sourceTasksByStatus.todo.filter(
            (t) => t.id !== draggedTask.id,
          )

          // Update indices for remaining source tasks
          const updatedSourceTasks = remainingSourceTasks.map(
            (task, index) => ({
              ...task,
              index,
              updatedAt: new Date().toISOString(),
            }),
          )

          allUpdatedTasks = [...updatedTargetTasks, ...updatedSourceTasks]
        }

        // Save all updated tasks using batch processing
        await saveItems(
          allUpdatedTasks.map((updatedTask) => ({
            itemId: updatedTask.id,
            type: SyncItemType.Task,
            content: updatedTask,
          })),
          DRAG_SAVE_DEBOUNCE,
        )
      } catch (error) {
        console.error("Failed to update task order:", error)
      }

      // Reset drag state after successful processing
      setDragState({
        draggedTaskId: null,
        draggedTask: null,
        sourceDate: null,
        originalIndex: null,
        hoverDate: null,
        hoverIndex: null,
        wasSuccessfulDrop: false,
      })
    },
    [dragState.sourceDate, tasksByDate, saveItems],
  )

  // Scroll to a specific date with smooth behavior
  const scrollToSpecificDate = useCallback(
    (date: string) => {
      const flatList = flatListRef.current
      if (!flatList) return

      // Find the index of the date in the current range
      const dateIndex = renderDateRange.findIndex((d) => d === date)
      if (dateIndex === -1) return

      flatList.scrollToIndex({
        index: dateIndex,
        animated: true,
      })

      // Monitor when scrolling stops
      manualScrollingFlagTimerRef.current = setTimeout(() => {
        manualScrollingFlagTimerRef.current = null
      }, DRAG_SCROLL_TIMEOUT)
    },
    [renderDateRange],
  )

  // Handle scroll event - only execute when scrolling has stopped
  const handleScroll = useCallback(
    (event: any) => {
      // Clear any existing timeout
      if (scrollStopTimeoutRef.current) {
        clearTimeout(scrollStopTimeoutRef.current)
      }

      // Set a new timeout to execute when scrolling stops
      scrollStopTimeoutRef.current = setTimeout(() => {
        requestAnimationFrame(() => {
          if (manualScrollingFlagTimerRef.current) return

          const { contentOffset } = event.nativeEvent
          const scrollX = contentOffset.x

          // Find the first fully visible date column
          const columnWidth = DAY_COLUMN_WIDTH
          let firstVisibleDate: string | null = null

          for (let i = 0; i < renderDateRange.length; i++) {
            const columnLeft = i * columnWidth

            // Check if this column is fully visible (left edge is visible)
            if (columnLeft >= scrollX) {
              firstVisibleDate = renderDateRange[i]
              break
            }
          }

          if (firstVisibleDate) {
            nextVisibleDateAfterUserScroll.current = firstVisibleDate
            onCurrentDateChange(firstVisibleDate)
          }
        })
      }, SCROLL_STOP_DELAY)
    },
    [renderDateRange, onCurrentDateChange],
  )

  // on load scroll to the selected date
  useEffect(() => {
    if (!isSameDate(currentDate, nextVisibleDateAfterUserScroll.current)) {
      delete nextVisibleDateAfterUserScroll.current
      scrollToSpecificDate(currentDate)
    }
  }, [currentDate, scrollToSpecificDate])

  // Update the date range when current date changes and is outside current range
  useEffect(() => {
    const firstDate = renderDateRange[0]
    const lastDate = renderDateRange[renderDateRange.length - 1]

    // Check if current date is outside the current range
    if (
      isBeforeDate(currentDate, addDaysToDate(firstDate, DATE_RANGE_BUFFER)) ||
      isAfterDate(currentDate, addDaysToDate(lastDate, -DATE_RANGE_BUFFER))
    ) {
      setIsLoading(true)

      // Use setTimeout to allow loading state to be shown
      setTimeout(() => {
        setRenderDateRange(generateNewDateRange(currentDate))
        setIsLoading(false)

        // After resetting the range, we need to scroll to the current date
        setTimeout(() => {
          scrollToSpecificDate(currentDate)
        }, LOADING_DELAY)
      }, LOADING_DELAY)
    }
  }, [currentDate, renderDateRange, scrollToSpecificDate])

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollStopTimeoutRef.current) {
        clearTimeout(scrollStopTimeoutRef.current)
      }
    }
  }, [])

  // Render each date column
  const renderDateColumn = useCallback(
    ({ item: date }: { item: string }) => {
      const dateItems = getTasksForDate(tasksByDate, date)
      const allDateItems = getTasksForDate(allTasksByDate, date)
      return (
        <DroppableDayColumn
          date={date}
          tasks={dateItems}
          allTasks={allDateItems}
          dragState={dragState}
          selectedTag={selectedTag}
          className="flex-shrink-0 flex flex-col"
          width={DAY_COLUMN_WIDTH}
        />
      )
    },
    [tasksByDate, allTasksByDate, dragState, selectedTag],
  )

  // Get item layout for FlatList optimization
  const getItemLayout = useCallback(
    (_data: any, index: number) => ({
      length: DAY_COLUMN_WIDTH,
      offset: DAY_COLUMN_WIDTH * index,
      index,
    }),
    [],
  )

  if (isLoading) {
    return (
      <Box className="flex-1 items-center justify-center">
        <Box className="text-primary w-12 h-12 mb-4">
          {/* TODO: Add spinner component */}
        </Box>
      </Box>
    )
  }

  return (
    <DragDropProvider
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <FlatList
        ref={flatListRef}
        data={renderDateRange}
        keyExtractor={(item) => item}
        renderItem={renderDateColumn}
        horizontal={true}
        className="flex-1 py-4 bg-background-0"
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={SCROLL_THROTTLE}
        getItemLayout={getItemLayout}
      />

      <DragOverlay
        dropAnimation={dragState.wasSuccessfulDrop ? null : undefined}
      >
        {dragState.draggedTask ? (
          <TaskItem task={dragState.draggedTask} />
        ) : null}
      </DragOverlay>
    </DragDropProvider>
  )
}
